// src/lib/http/authErrors.ts
// Reads the §11 error envelope off a failed request, for every MFA screen and both halves of the shared client.

import type { IdentityErrorBody } from "../../types/auth/mfa";

export interface ErrorEnvelope {
	code?: string;
	message?: string;
	details: Record<string, unknown>;
}

export interface AuthFailure extends ErrorEnvelope {
	status?: number;
}

// Never parses, so authFailure stays synchronous for the MFA screens; readErrorEnvelope decodes first.
export const errorEnvelope = (body: unknown): ErrorEnvelope => {
	const { error, message, details } = (body ?? {}) as Partial<IdentityErrorBody>;
	return {
		code: typeof error === "string" ? error : undefined,
		message: typeof message === "string" && message !== "" ? message : undefined,
		details: details && typeof details === "object" ? details : {},
	};
};

const blobText = (blob: Blob): Promise<string> =>
	typeof blob.text === "function"
		? blob.text()
		: new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = () => resolve(String(reader.result ?? ""));
				reader.onerror = () => reject(reader.error);
				reader.readAsText(blob);
			});

// A body can arrive as a Blob, ArrayBuffer or string (axios responseType, fetch text); one that does not decode has no envelope.
export const readErrorEnvelope = async (data: unknown): Promise<ErrorEnvelope> => {
	let body: unknown = data;
	try {
		if (typeof Blob !== "undefined" && body instanceof Blob) body = await blobText(body);
		else if (body instanceof ArrayBuffer) body = new TextDecoder().decode(body);
		if (typeof body === "string") body = body ? JSON.parse(body) : undefined;
	} catch {
		return { details: {} };
	}
	return errorEnvelope(body);
};

export const authFailure = (error: unknown): AuthFailure => {
	const response = (error as { response?: { status?: number; data?: unknown } })?.response;
	return { status: response?.status, ...errorEnvelope(response?.data) };
};

export const retryAfterSeconds = (failure: AuthFailure): number | undefined => {
	const value = Number(failure.details.retry_after);
	return Number.isFinite(value) && value > 0 ? value : undefined;
};

// details.fields is identity's 422 shape; the first message for that field, if any.
export const fieldError = (failure: AuthFailure, field: string): string | undefined => {
	const fields = failure.details.fields as Record<string, unknown> | undefined;
	const messages = fields?.[field];
	return Array.isArray(messages) && typeof messages[0] === "string" ? messages[0] : undefined;
};

export const hasFieldError = (failure: AuthFailure, field: string): boolean => {
	const fields = failure.details.fields as Record<string, unknown> | undefined;
	return Boolean(fields && field in fields);
};

export const attemptsRemaining = (failure: AuthFailure): number | undefined => {
	const value = Number(failure.details.attempts_remaining);
	return Number.isInteger(value) && value >= 0 ? value : undefined;
};

// The user dismissed the step-up prompt the shared client raised; the original request simply did not happen.
export const isStepUpDeclined = (error: unknown): boolean => authFailure(error).code === "stepup_required";

const FALLBACK = "Something went wrong. Please try again.";

// A message safe to show for any identity failure (§11 messages never carry PII or token material).
export const authFailureMessage = (error: unknown, fallback: string = FALLBACK): string => {
	const failure = authFailure(error);
	if (failure.code === "rate_limited") {
		const wait = retryAfterSeconds(failure);
		return wait ? `Too many attempts. Try again in ${wait} seconds.` : "Too many attempts. Please wait and try again.";
	}
	if (failure.status === undefined) return "We couldn't reach the server. Check your connection and try again.";
	return failure.message ?? fallback;
};
